import { useState, useRef } from 'react';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Camera, 
  Edit, 
  Key, 
  Bell, 
  ExternalLink, 
  Trophy, 
  Target, 
  Crown,
  Coins,
  Calendar,
  Users,
  Gamepad2
} from 'lucide-react';

export default function Profile() {
  const { user: authUser } = useAuth();
  const { userProfile, loading, error, updateProfile } = useUserProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: true
  });

  const [connectedAccounts] = useState({
    google: true,
    discord: false,
    apple: false
  });

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !authUser) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${authUser.id}-${Math.random()}.${fileExt}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      if (!data.publicUrl) throw new Error('Failed to get public URL');

      // Add cache bust parameter
      const avatarUrl = `${data.publicUrl}?v=${Date.now()}`;

      // Update profile using direct supabase call
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('user_id', authUser.id);

      if (updateError) throw updateError;

      toast.success('Avatar updated successfully!');
      
      // Force refresh to show new avatar
      window.location.reload();
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Mock stats data - replace with real data from your database
  const mockStats = {
    leaguesJoined: 24,
    wins: 18,
    losses: 6,
    highestRank: 3,
    totalRewards: 2450
  };

  const xpProgress = userProfile ? (userProfile.xp / (userProfile.level * 200)) * 100 : 60;

  const recentActivity = [
    { id: 1, action: "Joined League Championship 2024", time: "2 hours ago", icon: Users },
    { id: 2, action: "Drafted Mahomes in Fantasy Pro", time: "1 day ago", icon: Target },
    { id: 3, action: "Reached Level 3", time: "3 days ago", icon: Crown },
    { id: 4, action: "Won $50 in Daily Contest", time: "5 days ago", icon: Coins },
    { id: 5, action: "Completed Draft Strategy Guide", time: "1 week ago", icon: Gamepad2 }
  ];

  

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col">
          <AppHeader 
            userProfile={userProfile ? {
              username: userProfile.username,
              avatar_url: userProfile.avatar_url,
              victory_points: userProfile.victory_points,
              field_passes: userProfile.field_passes || 3
            } : undefined}
          />
          <main className="flex-1 p-6 bg-background">
            <div className="max-w-7xl mx-auto">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">Profile</h1>
                <p className="text-muted-foreground">Manage your account and view your stats</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Main Profile */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Profile Card */}
                  <Card className="bg-card border-border">
                    <CardContent className="p-8">
                      <div className="flex flex-col items-center text-center space-y-6">
                        {/* Avatar Section */}
                        <div className="relative group">
                          <Avatar className="h-32 w-32 border-4 border-primary/20">
                            <AvatarImage src={userProfile?.avatar_url} alt={userProfile?.username || 'User'} />
                            <AvatarFallback className="text-2xl bg-secondary">
                              {(userProfile?.username || authUser?.email || 'U').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div 
                            className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Camera className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                        
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          className="flex items-center gap-2"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingAvatar}
                        >
                          <Camera className="h-4 w-4" />
                          {uploadingAvatar ? 'Uploading...' : 'Change Avatar'}
                        </Button>

                        {/* User Info */}
                        <div className="space-y-2">
                          <h2 className="text-2xl font-bold text-foreground">{userProfile?.username || 'User'}</h2>
                          <p className="text-muted-foreground">{authUser?.email}</p>
                        </div>

                        <Button variant="outline" className="flex items-center gap-2">
                          <Edit className="h-4 w-4" />
                          Edit Profile
                        </Button>
                      </div>

                      <Separator className="my-8" />

                      {/* Stats Section */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground">Your Stats</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center p-4 rounded-lg bg-secondary/50">
                            <div className="flex items-center justify-center mb-2">
                              <Users className="h-5 w-5 text-primary" />
                            </div>
                            <div className="text-2xl font-bold text-foreground">{mockStats.leaguesJoined}</div>
                            <div className="text-sm text-muted-foreground">Leagues Joined</div>
                          </div>
                          
                          <div className="text-center p-4 rounded-lg bg-secondary/50">
                            <div className="flex items-center justify-center mb-2">
                              <Trophy className="h-5 w-5 text-success" />
                            </div>
                            <div className="text-2xl font-bold text-foreground">{mockStats.wins}-{mockStats.losses}</div>
                            <div className="text-sm text-muted-foreground">Win-Loss Record</div>
                          </div>
                          
                          <div className="text-center p-4 rounded-lg bg-secondary/50">
                            <div className="flex items-center justify-center mb-2">
                              <Crown className="h-5 w-5 text-warning" />
                            </div>
                            <div className="text-2xl font-bold text-foreground">#{mockStats.highestRank}</div>
                            <div className="text-sm text-muted-foreground">Highest Rank</div>
                          </div>
                          
                          <div className="text-center p-4 rounded-lg bg-secondary/50">
                            <div className="flex items-center justify-center mb-2">
                              <Coins className="h-5 w-5 text-primary" />
                            </div>
                            <div className="text-2xl font-bold text-foreground font-mono">
                              {userProfile?.victory_points?.toLocaleString() || '1,000'}
                            </div>
                            <div className="text-sm text-muted-foreground">Victory Points</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Widgets */}
                <div className="space-y-6">
                  {/* Victory Points Card - Prominent Display */}
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-primary">
                        <Trophy className="h-6 w-6" />
                        Total Victory Points
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center">
                        <div className="text-5xl font-bold font-mono text-primary mb-2">
                          {userProfile?.victory_points?.toLocaleString() || '1,000'}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Earned from competitions and achievements
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Account Settings Card */}
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Key className="h-5 w-5" />
                        Account Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <Button variant="outline" className="w-full justify-start">
                        <Key className="h-4 w-4 mr-2" />
                        Change Password
                      </Button>

                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Bell className="h-4 w-4" />
                          <span className="font-medium">Notification Preferences</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Email Notifications</span>
                            <Switch
                              checked={notifications.email}
                              onCheckedChange={(checked) =>
                                setNotifications(prev => ({ ...prev, email: checked }))
                              }
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Push Notifications</span>
                            <Switch
                              checked={notifications.push}
                              onCheckedChange={(checked) =>
                                setNotifications(prev => ({ ...prev, push: checked }))
                              }
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm">SMS Notifications</span>
                            <Switch
                              checked={notifications.sms}
                              onCheckedChange={(checked) =>
                                setNotifications(prev => ({ ...prev, sms: checked }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-3">
                          <ExternalLink className="h-4 w-4" />
                          <span className="font-medium">Connected Accounts</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm flex items-center gap-2">
                            Google
                            {connectedAccounts.google && <Badge variant="secondary" className="text-xs">Connected</Badge>}
                          </span>
                          <Button variant={connectedAccounts.google ? "destructive" : "default"} size="sm">
                            {connectedAccounts.google ? "Disconnect" : "Connect"}
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Discord</span>
                          <Button variant="default" size="sm" disabled={connectedAccounts.discord}>
                            Connect
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Apple</span>
                          <Button variant="default" size="sm" disabled={connectedAccounts.apple}>
                            Connect
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Field Pass Summary Card */}
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-warning" />
                        Field Pass Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Level {userProfile?.level || 1}</span>
                          <span className="text-sm text-muted-foreground">
                            {userProfile?.xp || 0}/{(userProfile?.level || 1) * 200} XP
                          </span>
                        </div>
                        <Progress value={xpProgress} className="h-2" />
                      </div>
                      
                      <Button variant="premium" className="w-full">
                        View Rewards Hub
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Recent Activity Card */}
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Recent Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {recentActivity.map((activity) => {
                        const IconComponent = activity.icon;
                        return (
                          <div
                            key={activity.id}
                            className="flex items-start gap-3 p-2 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                          >
                            <div className="mt-0.5">
                              <IconComponent className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground line-clamp-2">
                                {activity.action}
                              </p>
                              <p className="text-xs text-muted-foreground">{activity.time}</p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}